// ============================================================================
// EXECUTIVE / MIS DASHBOARD — MOCK DATA
// ============================================================================
// Hardcoded sample data for the management dashboard. Each export here
// mirrors the future API response shape so the widgets can be swapped to
// real endpoints by changing one import per widget.
//
// Numbers in INR. Periods are calendar months (apply filter logic later).
// All amounts in lakhs / crores when shown — formatters live in the widgets.
// ============================================================================

export interface RecoveryBookSnapshot {
  totalOutstanding: number;       // INR
  npaOutstanding: number;         // INR
  securedOutstanding: number;     // INR
  unsecuredOutstanding: number;   // INR
  actionableAccounts: number;     // count
  newThisPeriod: number;          // count of accounts moved into actionable
}

export interface AgingBucket {
  label: string;                  // "0-30", "31-60", ...
  accountCount: number;
  outstanding: number;            // INR
}

export interface CasePipelineStage {
  category: 'Early Recovery' | 'Recovery' | 'Litigation';
  stage: string;                  // free text — display label
  caseCount: number;
  outstanding: number;            // INR
}

export interface CashCollectedPoint {
  period: string;                 // "Jan", "Feb", "Mar"...
  collected: number;              // INR
  target: number;                 // INR
}

export interface SlaBreach {
  caseCode: string;
  caseType: string;               // display label
  stage: string;
  branch: string;
  daysInStage: number;
  tatDays: number;                // target TAT
  outstanding: number;
}

export interface TopMover {
  rank: number;
  branch: string;
  collectedThisPeriod: number;    // INR
  collectedLastPeriod: number;    // INR
  deltaPct: number;               // signed
  slippedAccountsThisPeriod: number;
}

/** Litigation cases PENDING FILING — the decision to litigate has been made
 *  but the plaint / petition has not yet been filed in court (matches the
 *  case_status = UNCLAIMED workflow state for litigation case types).
 *  Sliced by both age bucket and case type so the chart can render
 *  stacked-by-type bars with an OS overlay. Type name retains "Draft" as the
 *  code-level identifier for stability; UI labels say "Pending Filing". */
export interface LitigationDraftBucket {
  ageLabel: string;               // "0-7 days", "8-14", "15-30", "30+"
  /** Per case-type contribution within this age bucket. */
  byType: { caseType: string; caseCount: number; outstanding: number }[];
}

/** Active (post-claim) litigation cases bucketed by age since open + type. */
export interface LitigationActiveBucket {
  ageLabel: string;               // "0-30", "31-90", "91-180", "181-365", "365+"
  byType: { caseType: string; caseCount: number; outstanding: number }[];
}

/** Canonical litigation case-type display order + colour. Both Draft and Active
 *  widgets use this so the same colour means the same case type. */
export const LITIGATION_CASE_TYPES: { code: string; label: string; colour: string }[] = [
  { code: 'ARB',    label: 'Arbitration',        colour: '#2563eb' },
  { code: 'SAR',    label: 'SARFAESI',           colour: '#d97706' },
  { code: 'NIA',    label: 'NI Act §138',        colour: '#16a34a' },
  { code: 'EP',     label: 'Execution Petition', colour: '#8b5cf6' },
  { code: 'CIVIL',  label: 'Civil Suit',         colour: '#ec4899' },
];

/** Per-period new-vs-closed counts across ALL case types. */
export interface CaseFlowPoint {
  period: string;                 // "Dec", "Jan"...
  newCases: number;
  closedCases: number;
}

// ----------------------------------------------------------------------------
// Mock payloads — tuned to feel like a real mid-tier NBFC book (book ≈ 250 Cr).
// ----------------------------------------------------------------------------

export const mockRecoveryBook: RecoveryBookSnapshot = {
  totalOutstanding:     2_485_60_000,    // ~24.85 Cr
  npaOutstanding:         845_30_000,    //  ~8.45 Cr
  securedOutstanding:   1_920_40_000,
  unsecuredOutstanding:   565_20_000,
  actionableAccounts: 1_842,
  newThisPeriod: 127,
};

export const mockAgingBuckets: AgingBucket[] = [
  { label: '0-30',    accountCount:  486, outstanding:  324_50_000 },
  { label: '31-60',   accountCount:  312, outstanding:  287_80_000 },
  { label: '61-90',   accountCount:  259, outstanding:  256_40_000 },
  { label: '91-180',  accountCount:  428, outstanding:  587_60_000 },
  { label: '181-360', accountCount:  237, outstanding:  564_30_000 },
  { label: '360+',    accountCount:  120, outstanding:  465_00_000 },
];

export const mockCasePipeline: CasePipelineStage[] = [
  // Early Recovery
  { category: 'Early Recovery', stage: 'Unclaimed',           caseCount: 142, outstanding: 168_40_000 },
  { category: 'Early Recovery', stage: 'In Progress',         caseCount: 318, outstanding: 392_50_000 },
  { category: 'Early Recovery', stage: 'Commitment Received', caseCount:  87, outstanding: 102_30_000 },

  // Recovery
  { category: 'Recovery',       stage: 'Notice Issued',       caseCount:  64, outstanding: 198_50_000 },
  { category: 'Recovery',       stage: 'Repossession',        caseCount:  41, outstanding: 156_80_000 },
  { category: 'Recovery',       stage: 'Auction Listed',      caseCount:  18, outstanding:  78_40_000 },

  // Litigation
  { category: 'Litigation',     stage: 'Arbitration Filed',   caseCount:  29, outstanding: 215_60_000 },
  { category: 'Litigation',     stage: 'SARFAESI 13(2)',      caseCount:  22, outstanding: 312_40_000 },
  { category: 'Litigation',     stage: 'NI Act §138',         caseCount:  35, outstanding:  87_50_000 },
  { category: 'Litigation',     stage: 'Execution Petition',  caseCount:  11, outstanding: 142_60_000 },
];

export const mockCashCollected: CashCollectedPoint[] = [
  { period: 'Dec',  collected: 1_42_50_000, target: 1_50_00_000 },
  { period: 'Jan',  collected: 1_58_30_000, target: 1_50_00_000 },
  { period: 'Feb',  collected: 1_47_60_000, target: 1_60_00_000 },
  { period: 'Mar',  collected: 1_92_40_000, target: 1_75_00_000 },
  { period: 'Apr',  collected: 1_68_50_000, target: 1_80_00_000 },
  { period: 'May',  collected: 1_84_20_000, target: 1_80_00_000 },
];

export const mockSlaBreaches: SlaBreach[] = [
  { caseCode: 'PLC-2025-00184', caseType: 'Pre-Lit',     stage: 'In Progress',        branch: 'KA-BLR-001',  daysInStage: 92, tatDays: 30, outstanding: 28_50_000 },
  { caseCode: 'ARB-2025-00012', caseType: 'Arbitration', stage: 'Hearing Scheduled',  branch: 'MH-MUM-002',  daysInStage: 87, tatDays: 45, outstanding: 78_40_000 },
  { caseCode: 'SAR-2025-00045', caseType: 'SARFAESI',    stage: '13(2) Notice',       branch: 'TN-CHN-001',  daysInStage: 76, tatDays: 60, outstanding: 142_60_000 },
  { caseCode: 'PLC-2025-00321', caseType: 'Pre-Lit',     stage: 'Commitment Pending', branch: 'DEL-NCR-001', daysInStage: 65, tatDays: 30, outstanding:  18_40_000 },
  { caseCode: 'NIA-2025-00078', caseType: 'NI Act §138', stage: 'Notice Issued',      branch: 'GJ-AHM-001',  daysInStage: 58, tatDays: 30, outstanding:  12_50_000 },
];

export const mockTopMovers: TopMover[] = [
  { rank: 1, branch: 'MH-MUM-001', collectedThisPeriod: 32_40_000, collectedLastPeriod: 24_60_000, deltaPct:  31.7, slippedAccountsThisPeriod:  8 },
  { rank: 2, branch: 'KA-BLR-001', collectedThisPeriod: 28_70_000, collectedLastPeriod: 22_50_000, deltaPct:  27.6, slippedAccountsThisPeriod: 12 },
  { rank: 3, branch: 'TN-CHN-001', collectedThisPeriod: 24_50_000, collectedLastPeriod: 20_30_000, deltaPct:  20.7, slippedAccountsThisPeriod: 14 },
  { rank: 4, branch: 'GJ-AHM-001', collectedThisPeriod: 21_80_000, collectedLastPeriod: 24_20_000, deltaPct: -9.9, slippedAccountsThisPeriod: 24 },
  { rank: 5, branch: 'DEL-C-001',  collectedThisPeriod: 14_30_000, collectedLastPeriod: 19_80_000, deltaPct: -27.8, slippedAccountsThisPeriod: 31 },
];

/** Pending Filing — litigation cases whose plaint / petition has not yet
 *  been filed in court, broken down by age × case type. */
export const mockLitigationDraft: LitigationDraftBucket[] = [
  { ageLabel: '0-7', byType: [
      { caseType: 'Arbitration',        caseCount: 6, outstanding: 28_50_000 },
      { caseType: 'SARFAESI',           caseCount: 4, outstanding: 22_40_000 },
      { caseType: 'NI Act §138',        caseCount: 5, outstanding: 14_30_000 },
      { caseType: 'Execution Petition', caseCount: 2, outstanding: 12_50_000 },
      { caseType: 'Civil Suit',         caseCount: 1, outstanding:  4_70_000 },
  ]},
  { ageLabel: '8-14', byType: [
      { caseType: 'Arbitration',        caseCount: 3, outstanding: 18_60_000 },
      { caseType: 'SARFAESI',           caseCount: 4, outstanding: 25_80_000 },
      { caseType: 'NI Act §138',        caseCount: 3, outstanding:  9_40_000 },
      { caseType: 'Execution Petition', caseCount: 1, outstanding:  9_50_000 },
      { caseType: 'Civil Suit',         caseCount: 1, outstanding:  5_20_000 },
  ]},
  { ageLabel: '15-30', byType: [
      { caseType: 'Arbitration',        caseCount: 2, outstanding: 14_50_000 },
      { caseType: 'SARFAESI',           caseCount: 3, outstanding: 19_80_000 },
      { caseType: 'NI Act §138',        caseCount: 2, outstanding:  6_50_000 },
      { caseType: 'Execution Petition', caseCount: 1, outstanding:  8_20_000 },
      { caseType: 'Civil Suit',         caseCount: 1, outstanding:  5_20_000 },
  ]},
  { ageLabel: '30+', byType: [
      { caseType: 'Arbitration',        caseCount: 1, outstanding:  8_40_000 },
      { caseType: 'SARFAESI',           caseCount: 2, outstanding: 18_50_000 },
      { caseType: 'NI Act §138',        caseCount: 1, outstanding:  3_50_000 },
      { caseType: 'Execution Petition', caseCount: 1, outstanding:  9_80_000 },
      { caseType: 'Civil Suit',         caseCount: 1, outstanding:  5_60_000 },
  ]},
];

/** Active litigation — claimed cases by time since opening + type. */
export const mockLitigationActive: LitigationActiveBucket[] = [
  { ageLabel: '0-30', byType: [
      { caseType: 'Arbitration',        caseCount:  4, outstanding: 24_50_000 },
      { caseType: 'SARFAESI',           caseCount:  5, outstanding: 32_40_000 },
      { caseType: 'NI Act §138',        caseCount:  3, outstanding:  9_60_000 },
      { caseType: 'Execution Petition', caseCount:  1, outstanding: 12_50_000 },
      { caseType: 'Civil Suit',         caseCount:  1, outstanding:  7_40_000 },
  ]},
  { ageLabel: '31-90', byType: [
      { caseType: 'Arbitration',        caseCount:  9, outstanding: 72_50_000 },
      { caseType: 'SARFAESI',           caseCount:  8, outstanding: 65_30_000 },
      { caseType: 'NI Act §138',        caseCount:  8, outstanding: 28_40_000 },
      { caseType: 'Execution Petition', caseCount:  3, outstanding: 32_50_000 },
      { caseType: 'Civil Suit',         caseCount:  3, outstanding: 19_80_000 },
  ]},
  { ageLabel: '91-180', byType: [
      { caseType: 'Arbitration',        caseCount:  8, outstanding: 64_30_000 },
      { caseType: 'SARFAESI',           caseCount:  6, outstanding: 52_40_000 },
      { caseType: 'NI Act §138',        caseCount:  7, outstanding: 24_50_000 },
      { caseType: 'Execution Petition', caseCount:  3, outstanding: 35_60_000 },
      { caseType: 'Civil Suit',         caseCount:  2, outstanding: 18_50_000 },
  ]},
  { ageLabel: '181-365', byType: [
      { caseType: 'Arbitration',        caseCount:  5, outstanding: 48_50_000 },
      { caseType: 'SARFAESI',           caseCount:  4, outstanding: 42_40_000 },
      { caseType: 'NI Act §138',        caseCount:  4, outstanding: 18_50_000 },
      { caseType: 'Execution Petition', caseCount:  3, outstanding: 38_50_000 },
      { caseType: 'Civil Suit',         caseCount:  2, outstanding: 20_50_000 },
  ]},
  { ageLabel: '365+', byType: [
      { caseType: 'Arbitration',        caseCount:  2, outstanding: 22_30_000 },
      { caseType: 'SARFAESI',           caseCount:  2, outstanding: 24_50_000 },
      { caseType: 'NI Act §138',        caseCount:  1, outstanding:  4_50_000 },
      { caseType: 'Execution Petition', caseCount:  2, outstanding: 28_50_000 },
      { caseType: 'Civil Suit',         caseCount:  1, outstanding:  9_80_000 },
  ]},
];

/** Case flow — opened vs closed across all case types (count). */
export const mockCaseFlow: CaseFlowPoint[] = [
  { period: 'Dec', newCases: 142, closedCases: 118 },
  { period: 'Jan', newCases: 168, closedCases: 132 },
  { period: 'Feb', newCases: 155, closedCases: 148 },
  { period: 'Mar', newCases: 184, closedCases: 165 },
  { period: 'Apr', newCases: 172, closedCases: 158 },
  { period: 'May', newCases: 196, closedCases: 175 },
];

// ----------------------------------------------------------------------------
// Drilldown — Active Litigation case rows (synthesised to match bucket counts)
// ----------------------------------------------------------------------------

export interface ActiveLitigationCase {
  /** Required by SyutGrid — mirrors caseCode here. */
  id: string;
  caseCode: string;
  caseType: string;        // matches LITIGATION_CASE_TYPES.label
  ageBucket: string;       // matches mockLitigationActive.ageLabel
  ageDays: number;         // synthesised within the bucket
  borrowerName: string;
  branchCode: string;
  currentStage: string;
  nextHearing?: string;    // YYYY-MM-DD
  assignedAdvocate?: string;
  outstanding: number;
}

const CASE_TYPE_PREFIX: Record<string, string> = {
  'Arbitration':        'ARB',
  'SARFAESI':           'SAR',
  'NI Act §138':        'NIA',
  'Execution Petition': 'EP',
  'Civil Suit':         'CIV',
};

const STAGE_BY_TYPE: Record<string, string[]> = {
  'Arbitration':        ['Statement of Claim filed', 'Counterclaim received', 'Hearing scheduled', 'Award reserved'],
  'SARFAESI':           ['13(2) Notice issued', '60-day reply period', '13(4) Possession', 'DRT appeal'],
  'NI Act §138':        ['Notice issued', 'Complaint filed', 'Summons issued', 'Cross-examination'],
  'Execution Petition': ['EP filed', 'Notice to JD', 'Attachment ordered', 'Sale notification'],
  'Civil Suit':         ['Plaint filed', 'Written statement', 'Issues framed', 'Evidence stage'],
};

const BRANCH_POOL = [
  'KA-BLR-001', 'KA-BLR-002', 'MH-MUM-001', 'MH-MUM-002', 'TN-CHN-001',
  'DEL-C-001',  'DEL-NCR-001', 'GJ-AHM-001', 'WB-KOL-001', 'MH-PUNE-001',
];

const BORROWER_POOL = [
  'Rohan Mehta',      'Asha Pillai',     'Vikram Singh',    'Priya Iyer',
  'Sourav Chakraborty', 'Karthik Reddy', 'Anita Joshi',     'Hardik Patel',
  'Ritu Sharma',      'Manish Verma',    'Lakshmi Rao',     'Deepak Nair',
  'Pooja Agarwal',    'Sandeep Khanna',  'Meera Krishnan',  'Rajesh Gupta',
];

const ADVOCATE_POOL = [
  'V.K. Subramanian', 'Anjali Desai',    'P.R. Nair',       'Shalini Bose',
  'D.M. Joshi',       'A.K. Iyer',       'M. Krishnamurthy', 'Sunita Vohra',
];

const HEARING_DATES = [
  '2026-06-12', '2026-06-18', '2026-06-25', '2026-07-02',
  '2026-07-09', '2026-07-16', '2026-07-23',
];

const AGE_RANGE_BY_BUCKET: Record<string, [number, number]> = {
  '0-30':    [5, 28],
  '31-90':   [45, 88],
  '91-180':  [105, 175],
  '181-365': [200, 350],
  '365+':    [400, 720],
};

/** Deterministically generate one row per (bucket × type) count slot. The
 *  totals match mockLitigationActive exactly. Indexed picks (no Math.random)
 *  keep output stable across renders. */
function buildActiveLitigationCases(): ActiveLitigationCase[] {
  const rows: ActiveLitigationCase[] = [];
  let globalIdx = 0;
  for (const bucket of mockLitigationActive) {
    const [minAge, maxAge] = AGE_RANGE_BY_BUCKET[bucket.ageLabel] ?? [10, 30];
    for (const t of bucket.byType) {
      const prefix = CASE_TYPE_PREFIX[t.caseType] ?? 'CASE';
      const stages = STAGE_BY_TYPE[t.caseType] ?? ['Filed'];
      // Spread amount evenly across the count; vary slightly with index for realism.
      const perCaseAmount = Math.round(t.outstanding / Math.max(t.caseCount, 1));
      for (let i = 0; i < t.caseCount; i++, globalIdx++) {
        const ageSpread = maxAge - minAge;
        const ageDays   = minAge + (ageSpread > 0 ? (globalIdx * 7) % ageSpread : 0);
        const variance  = ((globalIdx % 5) - 2) * Math.round(perCaseAmount * 0.05);
        const code      = `${prefix}-2026-${String(10000 + globalIdx).slice(1)}`;
        rows.push({
          id:                 code,
          caseCode:           code,
          caseType:           t.caseType,
          ageBucket:          bucket.ageLabel,
          ageDays,
          borrowerName:       BORROWER_POOL[globalIdx % BORROWER_POOL.length],
          branchCode:         BRANCH_POOL  [globalIdx % BRANCH_POOL.length],
          currentStage:       stages       [globalIdx % stages.length],
          nextHearing:        HEARING_DATES[globalIdx % HEARING_DATES.length],
          assignedAdvocate:   ADVOCATE_POOL[globalIdx % ADVOCATE_POOL.length],
          outstanding:        Math.max(perCaseAmount + variance, 50_000),
        });
      }
    }
  }
  return rows;
}

export const mockActiveLitigationCases: ActiveLitigationCase[] = buildActiveLitigationCases();

/** Filter the active-litigation rows by any subset of (bucket, type). */
export const filterActiveLitigation = (
  ageBucket?: string,
  caseType?: string,
): ActiveLitigationCase[] =>
  mockActiveLitigationCases.filter(c =>
    (ageBucket == null || c.ageBucket === ageBucket) &&
    (caseType  == null || c.caseType  === caseType),
  );

// ----------------------------------------------------------------------------
// Filter options (mock)
// ----------------------------------------------------------------------------

export const PERIOD_OPTIONS = [
  { value: 'MTD',  label: 'Month to date' },
  { value: 'QTD',  label: 'Quarter to date' },
  { value: 'FYTD', label: 'FY to date' },
  { value: 'L3M',  label: 'Last 3 months' },
  { value: 'L6M',  label: 'Last 6 months' },
];

export const BRANCH_OPTIONS = [
  { value: '',             label: 'All branches' },
  { value: 'KA-BLR-001',   label: 'MG Road (KA-BLR-001)' },
  { value: 'KA-BLR-002',   label: 'Whitefield (KA-BLR-002)' },
  { value: 'MH-MUM-001',   label: 'Fort (MH-MUM-001)' },
  { value: 'MH-MUM-002',   label: 'Andheri East (MH-MUM-002)' },
  { value: 'TN-CHN-001',   label: 'T Nagar (TN-CHN-001)' },
  { value: 'DEL-C-001',    label: 'Connaught Place (DEL-C-001)' },
  { value: 'DEL-NCR-001',  label: 'Gurugram (DEL-NCR-001)' },
  { value: 'GJ-AHM-001',   label: 'Ashram Road (GJ-AHM-001)' },
  { value: 'WB-KOL-001',   label: 'Park Street (WB-KOL-001)' },
];

// ----------------------------------------------------------------------------
// Shared formatters (kept here so widgets render identically)
// ----------------------------------------------------------------------------

/** Format INR amount as 12.4 Cr / 38.5 L / ₹45,500. */
export const fmtAmount = (n: number): string => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

/** Format count with Indian grouping (1,42,500). */
export const fmtCount = (n: number): string =>
  n == null ? '—' : n.toLocaleString('en-IN');

/** Signed percent with arrow. */
export const fmtDeltaPct = (n: number): string => {
  if (n == null || isNaN(n)) return '—';
  const arrow = n > 0 ? '▲' : n < 0 ? '▼' : '•';
  return `${arrow} ${Math.abs(n).toFixed(1)}%`;
};
